import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Button } from '../Button/Button'
import { ButtonContainer } from '../ButtonContainer/ButtonContainer'

/**
 * A generic container component to display content inside a layer.
 * The InteractionLayer also be configured to act as a modal and
 * suspend user interaction outside its own context.
 *
 * @param children
 * @param {object} [button] - Configuration props of the button.
 * @param {React.ReactNodeArray} [buttonBarButtons] - A collection of {@link Button} items.
 * @param {boolean} [buttonInHeader]  - Hides the default close button and displays the {@link InteractionLayer.button}.
 * @param {string} [closeButtonLabel] - A label to use for the 'aria-label' attribute of the close button.
 * @param {boolean} [closeOnBackdrop] - A flag to control if the {@link InteractionLayer} should be
 *                                      closeable when the user clicks the backdrop of the layer.
 *                                      Only useful when combined with {@link modal}.
 * @param {string} [id] - An internal identifier.
 * @param {boolean} [isHidden] - Defines if the layer is visible/shown initially.
 * @param {boolean} [modal] - Defines if the {@link InteractionLayer} should act like a modal and
 *                            only allow user interaction within itself.
 * @param {('small'|'large')} [size] - The width size of the layer. Can be either 'small' or 'large'.
 * @param {function} [onClose] - Callback function to call after the {@link InteractionLayer} is closed.
 * @param {function} [onShow] - Callback function to call after the {@link InteractionLayer} is shown.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other properties to pass to the ButtonContainer element.
 * @return {JSX.Element} - The InteractionLayer element.
 * @constructor
 */
export function InteractionLayer({
  button,
  buttonBarButtons,
  buttonInHeader,
  children,
  closeButtonLabel,
  closeOnBackdrop,
  id,
  isHidden,
  modal,
  size,
  onClose,
  onShow,
  testId,
  ...otherProps
}) {
  const interactionLayerElement = useRef(null)

  /**
   * Manage active state to show editable mode.
   */
  const [isVisible, setIsVisible] = useState()

  /**
   * Set the {@see isVisible} state depending on the {@see isHidden} prop
   * and re-render is it gets changed.
   */
  useEffect(() => {
    setIsVisible(!isHidden)
    if (isVisible) {
      showInteractionLayer()
    }
  }, [isHidden, isVisible, setIsVisible, showInteractionLayer])

  /**
   * Hides the {@link InteractionLayer} and calls {@link onShow} callback.
   * @type {function(): void}
   */
  const hideInteractionLayer = useCallback(() => {
    setIsVisible(() => false)
    onClose && onClose()
  }, [setIsVisible, onClose])

  /**
   * Shows the {@link InteractionLayer} and calls {@link onShow} callback.
   * @type {function(): void}
   */
  const showInteractionLayer = useCallback(() => {
    setIsVisible(() => true)
    onShow && onShow()
  }, [setIsVisible, onShow])

  /**
   * Handles backdrop clicks. Only enabled when {modal} and {closeOnBackdrop} are set to true.
   * Only calls the {hideInteractionLayer} function if the target element actually matches.
   * @type {(function(*): void)|*}
   */
  const handleBackdropClick = useCallback(
    (event) => {
      if (modal && closeOnBackdrop && event?.target === interactionLayerElement.current) hideInteractionLayer()
    },
    [interactionLayerElement, hideInteractionLayer, modal, closeOnBackdrop]
  )

  /**
   * Additional button to render in and/or the header and footer section.
   * @type {JSX.Element}
   */
  const InteractionLayerButton = <Button {...button}>{button?.label}</Button>

  /**
   * The InteractionLayer close button.
   * @type {JSX.Element}
   */
  const InteractionLayerCloseButton = (
    <button
      className="c-interaction-layer__close-btn  js-interaction-layer-close  js-interaction-layer-autofocus"
      type="button"
      aria-label={closeButtonLabel}
      onClick={hideInteractionLayer}
    />
  )

  const divClassNameList = classNames('c-interaction-layer__wrapper', {
    [`c-interaction-layer--${size}`]: size,
    'c-interaction-layer--modal': modal,
  }).trim()

  /**
   * Conditionally display a sticky button bar.
   * @returns {JSX.Element}
   */
  const renderButtonBar = () => {
    return (
      <div className="c-interaction-layer__button-bar">
        <ButtonContainer centerSmall noWrap reverse stretch verticalSmall>
          {buttonBarButtons}
        </ButtonContainer>
      </div>
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={divClassNameList}
      aria-hidden={!isVisible}
      id={id}
      ref={interactionLayerElement}
      onClick={handleBackdropClick}
      data-testid={testId}
      {...otherProps}
    >
      {/* Backdrop element to act as a click area */}
      <div
        className="js-interaction-layer-close"
        tabIndex="-1"
        onClick={hideInteractionLayer}
        role="button"
        aria-label={closeButtonLabel}
      />

      <div className="c-interaction-layer" role="dialog">
        <header className="c-interaction-layer__header">
          {/* Conditionally display a custom button inside the InteractionLayer header. */}
          {buttonInHeader ? InteractionLayerButton : InteractionLayerCloseButton}
        </header>

        <div className="c-interaction-layer__content" role="document">
          {children}
        </div>

        {/* Conditionally display a custom button inside the InteractionLayer footer. */}
        {button && <div className="c-interaction-layer__footer">{InteractionLayerButton}</div>}
        {buttonBarButtons && renderButtonBar()}
      </div>
    </div>
  )
}

InteractionLayer.propTypes = {
  button: PropTypes.string, // Bronson template: 'button'.
  buttonBarButtons: PropTypes.arrayOf(PropTypes.node), // Bronson template: 'button-bar-buttons'.
  buttonInHeader: PropTypes.bool, // Bronson template: 'button-in-header'.
  children: PropTypes.node,
  closeButtonLabel: PropTypes.string,
  closeOnBackdrop: PropTypes.bool, // Bronson template: 'closeOnBackdrop'.
  id: PropTypes.string, // Bronson template: 'id'.
  isHidden: PropTypes.bool, // Bronson template: 'is-hidden'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /* Convenience props */
  modal: PropTypes.bool, // Convenience prop for c-interaction-layer--modal (Bronson template: 'modifier').
  size: PropTypes.oneOf(['small', 'large']), // Convenience prop for c-interaction-layer--modal (Bronson template: 'modifier').
  onClose: PropTypes.func, // Convenience prop.
  onShow: PropTypes.func, // Convenience prop.
}
